import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { Box } from "./MuiCompat";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { NewsItem as NewsItemType } from "../types/news";
import { newsService } from "../services/newsService";
import NewsItemContent from "./NewsItemContent";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const NewsItem: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [newsItem, setNewsItem] = useState<NewsItemType | null>(null);
  const [relatedItems, setRelatedItems] = useState<NewsItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNewsItem = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const fetchedItem = await newsService.getNewsBySlug(slug);

        if (!fetchedItem) {
          setError("News item not found");
          return;
        }

        setNewsItem(fetchedItem);

        // Fetch related items
        const allNews = await newsService.getAllNews();
        const related = allNews.results
          .filter((item: any) => item.slug !== slug)
          .sort(
            (a: any, b: any) =>
              new Date(b.published_date).getTime() -
              new Date(a.published_date).getTime()
          )
          .slice(0, 4);

        setRelatedItems(related);
        setError(null);
      } catch (err) {
        setError("Failed to load news item");
        console.error("Error fetching news item:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [slug]);

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "#fff8f2",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !newsItem) {
    return (
      <Box sx={{ bgcolor: "#fff8f2", color: "#4a4a4a", minHeight: "100vh" }}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            {error || "News item not found"}
          </Typography>
          <Link to="/news" style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                color: "#4a4a4a",
                mt: 2,
                "&:hover": { color: "#2a2a2a" },
              }}
            >
              <ArrowBackIcon sx={{ mr: 1, width: 16, height: 16 }} />
              <Typography variant="body2">Back to all news</Typography>
            </Box>
          </Link>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#fff8f2",
        color: "#4a4a4a",
        minHeight: "100vh",
        pt: { xs: "80px", md: "64px" },
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: "4xl", mx: "auto" }}>
          {/* Article Content */}
          <Box
            component="article"
            sx={{
              maxWidth: { xs: "100%", md: "75%" },
              mx: "auto",
              textAlign: "justify",
            }}
          >
            <Box sx={{ mb: 4, textAlign: "left" }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  fontWeight: "bold",
                  mb: 2,
                  color: "#1a1a1a",
                  fontFamily: "'Georgia', Times, 'Times New Roman', serif",
                }}
              >
                {newsItem.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "#4a4a4a", mb: 2 }}>
                By {newsItem.author.name} | {formatDate(newsItem.published_date)}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 8,
              }}
            >
              <Box sx={{ overflow: "hidden", borderRadius: "8px" }}>
                <img
                  src={newsItem.thumbnail_url}
                  alt={newsItem.title}
                  style={{
                    width: "100%",
                    maxWidth: 600,
                    maxHeight: 420,
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              </Box>
            </Box>

            {/* Dynamic Content */}
            <NewsItemContent item={newsItem} />
          </Box>

          {/* Continue Reading Section */}
          {relatedItems.length > 0 && (
            <Box
              sx={{
                mt: 8,
                pt: 4,
                borderTop: "1px solid",
                borderColor: "grey.400",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  mb: 4,
                  color: "#1a1a1a",
                  fontFamily: "'Georgia', Times, 'Times New Roman', serif",
                }}
              >
                More News
              </Typography>
              <Grid container spacing={3}>
                {relatedItems.map((item) => (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <Link
                      to={`/news/${item.slug}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Card
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          bgcolor: "#333",
                          borderRadius: "8px",
                          overflow: "hidden",
                          transition: "transform 0.3s",
                          "&:hover": {
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                          <CardMedia
                            component="img"
                            src={item.thumbnail_url}
                            alt={item.title}
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              maxHeight: 420,
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        </Box>
                        <CardContent sx={{ p: 2 }}>
                          <Typography
                            sx={{
                              fontWeight: "medium",
                              color: "#fff",
                              fontSize: "1rem",
                              lineHeight: 1.4,
                            }}
                          >
                            {item.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default NewsItem;
